import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { buttonVariants } from "@/components/ui/button";
import { config } from "@/data/config";
import { DISPOSABLE_TTL_OPTIONS } from "@/data/custom/disposable";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import { seo } from "@/utils/seo";

const sectionTitleClass =
	"text-sm font-semibold uppercase tracking-wide text-muted-foreground";
const proseClass =
	"text-base leading-7 text-pretty text-muted-foreground sm:text-lg sm:leading-8";

export const Route = createFileRoute("/_marketing/transparency")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Transparency",
			description: `What ${config.siteName} stores, how long it is kept, and how mailboxes are destroyed—in plain language.`,
			keywords:
				"privacy, data retention, disposable email, transparency, deletion, storage",
		}),
});

function Section({
	id,
	title,
	children,
	staggerIndex,
	className,
}: {
	id: string;
	title: string;
	children: ReactNode;
	/** Order on the page; drives staggered `animation-delay`. */
	staggerIndex: number;
	className?: string;
}) {
	return (
		<section
			id={id}
			className={cn("scroll-mt-24", MARKETING_SECTION_ANIM_BASE, className)}
			style={getStaggeredAnimationDelayStyle(staggerIndex, 0.16, 0.08)}
			aria-labelledby={`${id}-heading`}
		>
			<h2 id={`${id}-heading`} className={sectionTitleClass}>
				{title}
			</h2>
			<div className={cn("mt-4 space-y-4", proseClass)}>{children}</div>
		</section>
	);
}

export function RouteComponent() {
	const ttlList = DISPOSABLE_TTL_OPTIONS.map((o) => o.label.toLowerCase());

	return (
		<MarketingShell>
			<section className="pb-16 pt-20 sm:pb-24 sm:pt-28">
				<MarketingContainer>
					<div className="mx-auto max-w-2xl space-y-14 sm:space-y-16">
						<div>
							<MarketingHeader
								align="left"
								title="Transparency"
								heading="What I store and what disappears"
								description="This page is the technical truth as implemented today. If the product changes, this page should change with it—call me out if it drifts."
								className="mb-0 sm:mb-0"
								actions={
									<Link
										to="/"
										className={cn(
											buttonVariants({ variant: "outline", size: "default" }),
											"rounded-full px-5",
										)}
									>
										← Home
									</Link>
								}
							/>
						</div>

						<Section
							id="addresses"
							title="Disposable addresses"
							staggerIndex={0}
						>
							<p>
								Each address is a row in my database: the{" "}
								<strong className="font-medium text-foreground">
									local part
								</strong>{" "}
								(the bit before @), which{" "}
								<strong className="font-medium text-foreground">
									inbound hostname
								</strong>{" "}
								you picked from the allowed list, an{" "}
								<strong className="font-medium text-foreground">
									expiry time
								</strong>
								, and timestamps for creation and updates. There is no password
								and no account—whoever has the inbox URL can read that mailbox
								until it expires.
							</p>
							<p>
								I do not intentionally store your real identity when you create
								an address. Normal web requests may still carry an IP address to
								the server logs of whatever sits in front of the app (same as
								any website).
							</p>
						</Section>

						<Section id="messages" title="Inbound messages" staggerIndex={1}>
							<p>
								When mail arrives for an active address, I store metadata and
								body text:{" "}
								<strong className="font-medium text-foreground">From</strong>,{" "}
								<strong className="font-medium text-foreground">Subject</strong>
								,{" "}
								<strong className="font-medium text-foreground">
									plain text and HTML bodies
								</strong>{" "}
								(HTML is sanitized before storage), the message’s{" "}
								<strong className="font-medium text-foreground">
									RFC Message-ID
								</strong>{" "}
								when present (to reduce duplicates), and{" "}
								<strong className="font-medium text-foreground">
									received time
								</strong>
								. Attachment binaries are{" "}
								<strong className="font-medium text-foreground">not</strong>{" "}
								kept—only what fits in those text fields.
							</p>
							<p>
								The service is{" "}
								<strong className="font-medium text-foreground">
									inbound only
								</strong>
								: I never send outbound mail on your behalf from these
								addresses.
							</p>
							<p>
								Inbound delivery is built on top of{" "}
								<a
									href="https://github.com/baker/inboundparse"
									target="_blank"
									rel="noreferrer"
									className="font-medium text-primary underline-offset-4 hover:underline"
								>
									InboundParse
								</a>
								, which receives SMTP mail and forwards normalized webhook
								payloads to this app.
							</p>
						</Section>

						<Section id="ttl" title="How long you can choose" staggerIndex={2}>
							<p>
								When you create an address, you pick a lifetime. The presets
								today are: {ttlList.slice(0, -1).join(", ")}, and{" "}
								{ttlList.at(-1)}. That choice sets a single{" "}
								<strong className="font-medium text-foreground">
									expires at
								</strong>{" "}
								timestamp on the address row.
							</p>
							<p>
								After expiry, the inbox UI treats the mailbox as gone even if a
								background job has not finished yet—you should not see messages
								past that moment.
							</p>
						</Section>

						<Section
							id="destruction"
							title="Destruction process"
							staggerIndex={3}
						>
							<p>
								A scheduled worker job runs around the expiry time. It takes a
								database lock on that address, checks that the clock really has
								passed (with a small safety window), then in one transaction:{" "}
								<strong className="font-medium text-foreground">
									deletes every stored message
								</strong>{" "}
								for that address and sets a{" "}
								<strong className="font-medium text-foreground">
									destroyed at
								</strong>{" "}
								timestamp on the address. That is a hard delete from the
								application’s perspective, not a soft “archive.”
							</p>
							<p>
								If a job is delayed, there is also a sweep that can catch
								already-expired addresses that were missed and run the same
								deletion path.
							</p>
						</Section>

						<Section id="abuse" title="Abuse reports" staggerIndex={4}>
							<p>
								When someone uses{" "}
								<Link
									to="/"
									className="font-medium text-primary underline-offset-4 hover:underline"
								>
									Report abuse
								</Link>{" "}
								on the homepage, I store the{" "}
								<strong className="font-medium text-foreground">
									local part
								</strong>
								, free-text{" "}
								<strong className="font-medium text-foreground">reason</strong>,
								optional{" "}
								<strong className="font-medium text-foreground">
									contact email
								</strong>
								, and request{" "}
								<strong className="font-medium text-foreground">IP</strong> and{" "}
								<strong className="font-medium text-foreground">
									user agent
								</strong>{" "}
								for triage. Those rows are operator-facing; they are not tied to
								the disposable TTL above unless I delete them manually.
							</p>
						</Section>

						<Section id="feedback" title="Public feedback" staggerIndex={5}>
							<p>
								If you submit feedback through the public form, I store your{" "}
								<strong className="font-medium text-foreground">message</strong>
								, optional{" "}
								<strong className="font-medium text-foreground">email</strong>,{" "}
								request IP and user agent, and a small JSON metadata blob (for
								example which page you were on). I use that only to improve the
								product or reply—see{" "}
								<Link
									to="/contact"
									className="font-medium text-primary underline-offset-4 hover:underline"
								>
									Contact
								</Link>{" "}
								if you want email instead.
							</p>
							<p>
								The server may also enqueue an out-of-band notification (for
								example to a private operator channel) so I see your note
								quickly; that does not change what is persisted in the database
								described above.
							</p>
						</Section>

						<Section id="stats" title="Public counters" staggerIndex={6}>
							<p>
								The marketing site can show aggregate counts (addresses created,
								messages received, domains offered). Those are{" "}
								<strong className="font-medium text-foreground">
									numbers only
								</strong>
								—no per-address detail in that widget.
							</p>
						</Section>

						<Section id="limits" title="What this page is not" staggerIndex={7}>
							<p>
								I cannot promise what a host provider retains. I can promise
								what the code does: when destruction runs, the alias’s messages
								are removed and the alias is marked destroyed.
							</p>
							<p>
								Today I{" "}
								<strong className="font-medium text-foreground">do not</strong>{" "}
								back up databases or email messages. If the server falls over or
								something goes wrong, data can be lost—I treat that as an
								acceptable trade for a small disposable-mail experiment, not a
								archive service.
							</p>
							<p>
								If you need something removed urgently, use Report abuse with
								the local part, or write via{" "}
								<a
									href={`mailto:${config.supportEmail}`}
									className="font-medium text-primary underline-offset-4 hover:underline"
								>
									{config.supportEmail}
								</a>
								.
							</p>
						</Section>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
